import { BaseText, Button, theme } from 'aimo-ui';
import InfoBox from 'components/common/InfoBox';
import SvgImage from 'components/svgImage/SvgImage';
import useTutorialSheet from 'hooks/useTutorialSheet';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions } from 'react-native';
import Animated, {
  Easing,
  SlideOutLeft,
  useAnimatedStyle,
  useSharedValue,
  withDelay,
  withTiming,
} from 'react-native-reanimated';
import styled from 'styled-components/native';
import { Tutorial } from 'types/states';
import { isWeb } from 'utils/deviceUtils';
import { completeTutorial, openTutorial } from 'utils/tutorialUtils';
import { RemoteTutorial } from './RemoteTutorial';

const SCREEN_WIDTH = Dimensions.get('window').width;

const Row = styled.View`
  flex-direction: row;
`;

const Column = styled.View`
  flex-direction: column;
  flex: 1;
  padding: 0px 40px;
  gap: 10px;
`;

const ImageContainer = styled.View`
  margin-right: -20px;
`;

const ButtonContainer = styled.View`
  flex-direction: row;
  gap: 10px;
`;

const isRemote = (
  tutorial: Tutorial | RemoteTutorial
): tutorial is RemoteTutorial => {
  return !!(tutorial as RemoteTutorial).tutorialId;
};

const InfoButton = ({
  tutorial,
  lastTutorial,
}: {
  tutorial: Tutorial | RemoteTutorial;
  lastTutorial: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const tutorialToOpen = isRemote(tutorial) ? tutorial.tutorialId : tutorial;

  const translateX = useSharedValue(isWeb ? 0 : SCREEN_WIDTH);
  const opacity = useSharedValue(isWeb ? 1 : 0);

  useEffect(() => {
    translateX.value = withDelay(
      300,
      withTiming(0, { duration: 350, easing: Easing.ease })
    );
    opacity.value = withDelay(300, withTiming(1, { duration: 350 }));
  }, [translateX, opacity]);

  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
    opacity: opacity.value,
  }));

  const handleOpenTutorial = useCallback(() => {
    openTutorial(tutorialToOpen);
  }, [tutorialToOpen]);

  const handleCompleteTutorial = useCallback(() => {
    completeTutorial(tutorialToOpen);
  }, [tutorialToOpen]);

  return (
    <Animated.View
      pointerEvents="box-none"
      style={animatedStyle}
      exiting={
        lastTutorial || isWeb
          ? undefined
          : SlideOutLeft.duration(350).easing(Easing.ease)
      }>
      <InfoBox padding={15} margin={10} bgColor={theme.colors.lightBg.green}>
        <Row>
          <ImageContainer>
            <SvgImage size="xxl" name="Tutorial_Car" testID={'tutorial-card'} />
          </ImageContainer>
          <Column>
            <BaseText>{t(`tutorial.${tutorialToOpen}.title`)}</BaseText>
            <ButtonContainer>
              <Button
                buttonType="primary"
                fullWidth={false}
                text={
                  i18n.exists(`tutorial.${tutorialToOpen}.accept`)
                    ? t(`tutorial.${tutorialToOpen}.accept`)
                    : t('tutorial.firstTimer.accept')
                }
                size="m"
                onPress={handleOpenTutorial}
              />
              <Button
                buttonType="tertiary"
                fullWidth={false}
                text={
                  i18n.exists(`tutorial.${tutorialToOpen}.decline`)
                    ? t(`tutorial.${tutorialToOpen}.decline`)
                    : t('tutorial.firstTimer.decline')
                }
                size="m"
                onPress={handleCompleteTutorial}
              />
            </ButtonContainer>
          </Column>
        </Row>
      </InfoBox>
    </Animated.View>
  );
};

const OpenTutorialButton = () => {
  const {
    canOpenBundledTutorial,
    canOpenRemoteTutorial,
    unCompletedRemoteTutorials,
  } = useTutorialSheet({
    selectedTutorial: 'firstTimer',
  });

  const combinedTutorial =
    canOpenBundledTutorial ?? canOpenRemoteTutorial ?? '';

  const lastTutorial =
    unCompletedRemoteTutorials.length + (canOpenBundledTutorial ? 1 : 0) <= 1;

  if (combinedTutorial) {
    return (
      <InfoButton tutorial={combinedTutorial} lastTutorial={lastTutorial} />
    );
  }
  return <></>;
};

export default OpenTutorialButton;
